<script setup lang="ts">import { ref as _ref, toRef as _toRef } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'
import { useDefaultLocation } from '@consumer/services/api/customizer'
import { useElementSize } from '@vueuse/core'
import { last } from '@consumer/helpers/array'
import { faSearch, faLocationDot } from '@fortawesome/pro-light-svg-icons'

withDefaults(defineProps<{ gift: GiftTemplate }>(), {  })

const emit = defineEmits<{
  submit: [searchTerms: string, searchLocation: string]
  reset: []
}>()

let searchTerm = _ref<string>()
let searchLocation = (useDefaultLocation())

function onSubmit () {
  onBlurLocation()
  emit('submit', searchTerm.value!, searchLocation.value!)
}

function onReset () {
  searchTerm.value = undefined
  emit('reset')
}

onMounted(() => {
  if (__props.gift.giftlyMetadata?.placeSearchTerms && __props.gift.giftlyMetadata?.placeSearchLocations) {
    searchTerm.value = last(__props.gift.giftlyMetadata?.placeSearchTerms)
    searchLocation.value = last(__props.gift.giftlyMetadata?.placeSearchLocations)
    onSubmit()
  }
})

let focusSearchTerm = _ref(false)
let focusSearchLocation = _ref(false)

function onBlurLocation () {
  focusSearchTerm.value = false
  focusSearchLocation.value = false
}

function onBlurSearch () {
  if (searchTerm.value?.length === 0) {
    searchTerm.value = undefined
  }
}

const searchOverlayRef = ref<HTMLDivElement>()
const __$temp_1 = (useElementSize(searchOverlayRef)),
  searchOverlayWidth = _toRef(__$temp_1, 'width');
const searchOverlayLocationRef = ref<HTMLDivElement>()
const __$temp_2 = (useElementSize(searchOverlayLocationRef)),
  searchLocationPlaceholderWidth = _toRef(__$temp_2, 'width');

const searchTermWidth = computed(() => searchOverlayWidth.value - searchLocationPlaceholderWidth.value - 50)
</script>

<template>
  <form class="w-full flex flex-col lg:flex-row gap-4" @submit.prevent="onSubmit">
    <div class="flex-grow relative">
      <GTextInput
        v-model="searchTerm"
        placeholder="Search 'restaurants', 'spas', etc"
        :icon="faSearch"
        name="nc_place_search_input"
        enterkeyhint="search"
        :class="searchTerm ? '!pr-10 !focus:pr-10' : '!pr-0 !focus:pr-0'"
        @focus="focusSearchTerm = true"
        @blur="onBlurSearch"
      >
        <template #addonRight>
          <ClearTextButton v-show="searchTerm" name="nc_place_clear_search" @click="onReset"/>
        </template>
      </GTextInput>
      <div
        v-if="searchTerm && !focusSearchTerm"
        ref="searchOverlayRef"
        class="lg:hidden absolute inset-0 pointer-events-none flex items-center ml-10"
      >
        <div class="bg-white inline-flex gap-2">
          <div
            class="whitespace-nowrap overflow-hidden overflow-ellipsis"
            :style="{ maxWidth: `${searchTermWidth}px`}"
          >
            {{ searchTerm }}
          </div>
          <div ref="searchOverlayLocationRef" class="flex-1 whitespace-nowrap text-grey-700">
            {{ searchLocation }}
          </div>
        </div>
      </div>
    </div>
    <div class="hidden lg:flex gap-4" :class="{ '!flex': focusSearchTerm || focusSearchLocation }">
      <div class="hidden lg:flex items-center">in</div>
      <div class="flex-grow">
        <GTextInput
          v-model="searchLocation"
          name="nc_place_location_input"
          placeholder="City or Zip Code"
          :icon="faLocationDot"
          enterkeyhint="search"
          @focus="focusSearchLocation = true"
          @blur="onBlurLocation"
        >
          <template #addonRight>
            <ClearTextButton
              v-show="searchLocation"
              name="nc_place_clear_location"
              @click="searchLocation = undefined"
            />
          </template>
        </GTextInput>
      </div>
      <GButton
        primary
        type="submit"
        size="large"
        name="nc_place_search"
        class="hidden lg:flex g-button-no-label flex-shrink-0"
      >
        <GIcon :icon="faSearch" size="md"/>
        <span class="hidden">{{ searchTerm }} in {{ searchLocation }}</span>
      </GButton>
    </div>
  </form>
</template>
